import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, Loader2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/kit";
import { GardenScene } from "@/components/garden/GardenScene";
import { svgToPngBlob, shareOrDownload } from "@/lib/gardenShare";
import { moodEmoji, moodLabel } from "@/lib/mood";
import { SEASON_LOOK } from "@/components/garden/Seasons";
import { HeartBack } from "@/components/HeartBack";

const SEEN = (m) => `mm-garden-seen-${m}`;
const LEGEND = [["sunflower", "🌻", "Great days"], ["tulip", "🌷", "Good days"], ["lavender", "💜", "Calm, okay days"], ["bluebell", "🔵", "Low days"], ["snowdrop", "🤍", "Hard days, still growing"]];

const GoldenDetail = ({ f, onClose }) => {
  const [note, setNote] = useState(null);
  const show = () => api.get(`/garden/golden/${f.id}`).then((r) => setNote(r.data)).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-3xl p-4 flex items-start gap-3 mm-fade-up shadow-sm border border-[#F6C343]/40" style={{ background: "#FFF6D6" }} data-testid="garden-golden-detail">
      <div className="text-3xl" aria-hidden>🌼</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold">A good moment</div>
        <div className="text-sm text-mm-muted">{new Date(`${f.day}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} · from your gratitude jar</div>
        {note ? <p className="mt-2 break-words [overflow-wrap:anywhere]" data-testid="garden-golden-text">{note.emoji} {note.text}</p>
          : <button className="text-sm text-mm-primary font-semibold mt-2" onClick={show} data-testid="garden-golden-show">Show note</button>}
      </div>
      <button onClick={onClose} className="p-1 text-mm-muted" aria-label="Close" data-testid="garden-flower-detail-close"><X size={16} /></button>
    </div>
  );
};

const FRIEND_LOOK = { friendrose: ["💗", "Friendship rose"], forgetmenot: ["💜", "Forget-me-not"], blossom: ["🌸", "Cherry blossom"] };

const FriendDetail = ({ f, onClose }) => {
  const [note, setNote] = useState(null);
  useEffect(() => { api.get(`/garden/golden/${f.id}`).then((r) => setNote(r.data)).catch((e) => toast.error(errMsg(e))); }, [f.id]);
  const [emoji, label] = FRIEND_LOOK[f.kind] || FRIEND_LOOK.friendrose;
  return (
    <div className="rounded-3xl p-4 flex items-start gap-3 mm-fade-up shadow-sm border border-[#F28FAD]/40" style={{ background: "#FFF0F5" }} data-testid="garden-friend-detail">
      <div className="text-3xl" aria-hidden>{emoji}</div>
      <div className="flex-1 min-w-0 text-[#3A2E4F]">
        <div className="font-semibold" data-testid="garden-friend-from">{label} from {note?.from_name || f.from_name || "a supporter"}</div>
        <div className="text-sm opacity-70">{new Date(`${f.day}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
        {note ? <p className="mt-2 break-words [overflow-wrap:anywhere]" style={{ fontFamily: "Georgia, serif" }} data-testid="garden-friend-text">“{note.text}”</p>
          : <Loader2 size={16} className="animate-spin mt-2 text-mm-primary" />}
        {note && <div className="mt-2"><HeartBack id={f.id} hearted={note.hearted} name={note.from_name || "your supporter"} testId="garden-friend-heart" /></div>}
      </div>
      <button onClick={onClose} className="p-1 text-mm-muted" aria-label="Close" data-testid="garden-flower-detail-close"><X size={16} /></button>
    </div>
  );
};

const FlowerDetail = ({ f, onClose }) => f.friend ? <FriendDetail key={f.id} f={f} onClose={onClose} /> : f.golden ? <GoldenDetail key={f.id} f={f} onClose={onClose} /> : (
  <div className="mm-card p-4 flex items-start gap-3 mm-fade-up" data-testid="garden-flower-detail">
    <div className="text-3xl">{moodEmoji(f.mood)}</div>
    <div className="flex-1">
      <div className="font-semibold">{new Date(`${f.day}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
      <div className="text-sm text-mm-muted">Mood {f.mood}/10 · {moodLabel(f.mood)}. Only you can see this.</div>
    </div>
    <button onClick={onClose} className="p-1 text-mm-muted" aria-label="Close" data-testid="garden-flower-detail-close"><X size={16} /></button>
  </div>
);

const MonthNav = ({ data, setMonth }) => {
  const i = data.months.indexOf(data.month);
  const older = data.months[i + 1], newer = data.months[i - 1];
  return (
    <div className="flex items-center gap-2">
      <button className="mm-btn-ghost !px-2" disabled={!older} onClick={() => setMonth(older)} aria-label="Previous month" data-testid="garden-prev-month"><ChevronLeft size={18} /></button>
      <span className="font-semibold min-w-[8.5rem] text-center" data-testid="garden-month-label">{data.label}</span>
      <button className="mm-btn-ghost !px-2" disabled={!newer} onClick={() => setMonth(newer)} aria-label="Next month" data-testid="garden-next-month"><ChevronRight size={18} /></button>
    </div>
  );
};

export default function Garden() {
  const [month, setMonth] = useState(null);
  const [data, setData] = useState(null);
  const [seenAt, setSeenAt] = useState(null);
  const [picked, setPicked] = useState(null);
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    setPicked(null);
    let live = true;
    api.get("/garden", { params: month ? { month } : {} }).then(({ data: d }) => {
      if (!live) return;
      setSeenAt(localStorage.getItem(SEEN(d.month)) || "");
      setData(d);
      const latest = [...d.flowers, ...d.greens].map((x) => x.at).sort().pop();
      if (latest) localStorage.setItem(SEEN(d.month), latest);
    }).catch((e) => live && toast.error(errMsg(e, "Couldn't load your garden")));
    return () => { live = false; };
  }, [month]);

  const share = async () => {
    setSharing(true);
    try {
      const blob = await svgToPngBlob(shareRef.current);
      const res = await shareOrDownload(blob, `my-${data.month_name.toLowerCase()}-garden.png`, `My ${data.month_name} garden`);
      if (res === "downloaded") toast.success("Garden picture saved 🌸");
    } catch (e) { toast.error(e.message); }
    finally { setSharing(false); }
  };

  const shareData = useMemo(() => data && { ...data, skill_streak: 0, hugs: [] }, [data]);
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-mm-primary" /></div>;
  const golden = data.golden?.length || 0;
  const friends = data.friends?.length || 0;
  const empty = !data.flowers.length && !data.greens.length && !golden && !friends;
  return (
    <div className="max-w-4xl" data-testid="garden-page">
      <PageHeader back title="My Garden" subtitle="Every check-in plants a flower, gratitude notes grow golden marigolds, notes from your supporters grow friendship flowers, journal entries add greenery, and coping skills bring butterflies." />
      <div className="flex flex-wrap items-center justify-center sm:justify-between gap-3 mb-3">
        {SEASON_LOOK[data.season] && <span className="mm-chip !text-sm pointer-events-none" data-testid="garden-season">{SEASON_LOOK[data.season].emoji} {SEASON_LOOK[data.season].label}</span>}
        {data.hugs?.length > 0 && <Link to="/hugs" className="mm-chip !text-sm hover:scale-105 transition-transform" data-testid="garden-hug-count">🤗 {data.hugs.length} hug{data.hugs.length === 1 ? "" : "s"} from supporters</Link>}
        {data.skill_streak > 0 && <span className="mm-chip !text-sm pointer-events-none" data-testid="garden-skill-streak">✨ {data.skill_streak}-day skill streak</span>}
        <MonthNav data={data} setMonth={setMonth} />
      </div>
      <div className="mm-card overflow-hidden relative" data-testid="garden-scene">
        <GardenScene data={data} seenAt={seenAt} onPick={setPicked} interactive />
        {empty && (
          <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="garden-empty">
            <div className="bg-white/90 rounded-3xl px-6 py-5 text-center max-w-sm">
              <div className="font-semibold">{data.is_current ? "Your garden is ready for its first flower" : "Nothing grew this month"}</div>
              {data.is_current && <><p className="text-sm text-mm-muted mt-1">Check in to plant one. It takes about two minutes.</p><Link to="/checkin" className="mm-btn mt-3 inline-flex" data-testid="garden-checkin-cta">Check in</Link></>}
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
        <p className="text-sm text-mm-muted" data-testid="garden-counts">{data.flowers.length} flower{data.flowers.length === 1 ? "" : "s"} · {golden} golden · <span data-testid="garden-friend-count">Friendship flowers: {friends}</span> · {data.greens.length} fern{data.greens.length === 1 ? "" : "s"} · {data.butterflies.length} butterfl{data.butterflies.length === 1 ? "y" : "ies"}{!empty && " · tap a flower to see its day"}</p>
        {!empty && <button className="mm-btn-ghost text-sm" onClick={share} disabled={sharing} data-testid="garden-share-btn">{sharing ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}Save share picture</button>}
      </div>
      {picked && <div className="mt-4"><FlowerDetail f={picked} onClose={() => setPicked(null)} /></div>}
      <div className="mm-card p-5 mt-6">
        <div className="font-semibold text-sm mb-3">What grows here</div>
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm" data-testid="garden-legend">
          {LEGEND.map(([k, e, l]) => <span key={k} className="inline-flex items-center gap-1.5"><span>{e}</span>{l}</span>)}
          <span className="inline-flex items-center gap-1.5">🌼 Your gratitude notes</span>
          {Object.entries(FRIEND_LOOK).map(([k, [e, l]]) => <span key={k} className="inline-flex items-center gap-1.5" data-testid={`garden-legend-${k}`}>{e} {l} (supporter note)</span>)}<span className="inline-flex items-center gap-1.5">🌿 Journal entries</span><span className="inline-flex items-center gap-1.5">🦋 Coping skills used</span><span className="inline-flex items-center gap-1.5">🤗 Streak hugs from supporters (floating hearts)</span><span className="inline-flex items-center gap-1.5">✨ Skill streak days</span>
        </div>
        <p className="text-xs text-mm-muted mt-3">The share picture shows only your flowers and the month. No moods, dates or words.</p>
      </div>
      <div className="fixed -left-[9999px] top-0 w-[800px]" aria-hidden><GardenScene ref={shareRef} data={shareData} title={`My ${data.month_name} garden`} /></div>
    </div>
  );
}
